import { Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import * as assetsActions from '@api-ui-app/src/app/ngrx/assets.actions';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { SubscriptionsFacade } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.facade';
import { logEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { EditorInboundEventType } from '@gelato-api-ui/core/design-editor/types/editor-inbound-event-type.enum';
import { Asset } from '@gelato-api-ui/core/designs/asset';
import { AssetCategory } from '@gelato-api-ui/core/designs/asset-api';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { getUsedAssets } from '@gelato-api-ui/core/designs/helpers/getUsedAssets';
import { mapAssetsToCollection } from '@gelato-api-ui/core/designs/helpers/mapAssetsToCollection';
import { getDesignMedia } from '@gelato-api-ui/core/designs/helpers/media/getDesignMedia';
import { AssetsApiService } from '@gelato-api-ui/core/designs/services/assets-api.service';
import { DesignGraphQlService } from '@gelato-api-ui/core/designs/services/design-graph-ql.service';
import { UploadedImageDataService } from '@gelato-api-ui/core/designs/services/uploaded-image-data.service';
import { PrintAreaToDisable } from '@gelato-api-ui/core/designs/types/print-areas';
import { ExportedVariant } from '@gelato-api-ui/core/e-commerce/exported-variant';
import { DesignEditorCommunicationService } from '@gelato-api-ui/design-editor/services/design-editor-communication.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DesignEditorSharedService {
  selectedClientId$ = this.store$.select(getSelectedClientId);

  fetchedAssetIds = [];
  ASSETS_AMOUNT_TO_FETCH = 100;

  constructor(
    private store$: Store<AppState>,
    private readonly assetsApiService: AssetsApiService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly designEditorCommunicationService: DesignEditorCommunicationService,
    private readonly uploadedImageDataService: UploadedImageDataService,
    private readonly designGraphQlService: DesignGraphQlService,
    private readonly translate: TranslateService,
  ) {}

  addUploadedAssetsToEditor(hasEmbroidery: boolean, designAssets?: Asset[]): Observable<Asset[]> {
    return this.selectedClientId$.pipe(
      switchMap(clientId =>
        this.fetchAssets(
          clientId,
          hasEmbroidery ? [AssetCategory.Embroidery, AssetCategory.Print] : [AssetCategory.Print],
        ),
      ),
      map(fetchedAssets => R.uniqBy(loopAsset => loopAsset?.id, [...(designAssets || []), ...(fetchedAssets || [])])),
      tap(assets => {
        this.setAssets(assets, false);
      }),
    );
  }

  trackImageAddedToDesign(asset: Asset) {
    if (!asset) {
      return;
    }
    const { assetProviderUid, id } = asset;
    logEvent('editorClickAddImage', {
      assetProviderUid,
      addedThisSession: !this.fetchedAssetIds.includes(id),
    });
  }

  /*
   * Should return true if no change is needed
   * */
  checkDesignChangeForPrice(prev, next) {
    const [prevDS, prevAssets, , , prevDimensions] = prev;
    const [nextDS, nextAssets, , , nextDimensions] = next;
    const toString = (arr: Asset[]) => arr.map(e => e.id).join(',');
    const usedPrevAssets = toString(getUsedAssets(prevAssets, prevDS));
    const usedNextAssets = toString(getUsedAssets(nextAssets, nextDS));
    const sameAmountOfMedia = getDesignMedia(prevDS).length === getDesignMedia(nextDS).length;
    const sameAssetsUsed = usedPrevAssets === usedNextAssets;
    const isSizeWasNotChanged = R.equals(prevDimensions, nextDimensions);

    return sameAmountOfMedia && sameAssetsUsed && isSizeWasNotChanged;
  }

  disablePrintAreasWithConflicts(designStructure: DesignStructure): void {
    this.designGraphQlService
      .getPrintAreasToDisable(designStructure)
      .pipe(
        take(1),
        map(printAreasToDisable => this.replaceTranslationKeysWithTranslations(printAreasToDisable)),
      )
      .subscribe(printAreasToDisable =>
        this.designEditorCommunicationService.sendPostMessage(
          EditorInboundEventType.setDisabledSpreads,
          printAreasToDisable,
        ),
      );
  }

  private setAssets(assets: Asset[], useOriginalIfNoDefault: boolean) {
    this.store$.dispatch(new assetsActions.SetAssets(mapAssetsToCollection(assets)));
    this.sendAssetsToEditor(assets, useOriginalIfNoDefault);
  }

  assetsToUploadedImage(assets: Asset[], gelatoPlusOrGoldIsActive: boolean, useOriginalIfNoDefault = false) {
    return this.uploadedImageDataService.mapAssetsToUploadedImagesData(
      assets,
      gelatoPlusOrGoldIsActive,
      useOriginalIfNoDefault,
    );
  }

  updateDisabledPrintAreasForSelectedVariations(selectedVariations$: Observable<ExportedVariant[]>) {
    selectedVariations$
      .pipe(
        take(1),
        map(variations => variations?.[0]),
        filter(firstVariation => Boolean(firstVariation?.designStructureJson)),
        map(firstVariation => {
          const { designStructureJson } = firstVariation; // as all variations will be the same
          const designData: DesignStructure = JSON.parse(designStructureJson);
          this.disablePrintAreasWithConflicts(designData);
        }),
      )
      .subscribe();
  }

  private replaceTranslationKeysWithTranslations(printAreasToDisable: PrintAreaToDisable[]): PrintAreaToDisable[] {
    // transform data's keys to translations strings
    return [
      ...printAreasToDisable.map(printArea => {
        let { trKey, used, hovered, productionMethod } = printArea.information;
        used = this.translate.instant(used);
        hovered = this.translate.instant(hovered);
        productionMethod = this.translate.instant(productionMethod);

        const information = this.translate.instant(trKey, { used, hovered, productionMethod });
        return {
          ...printArea,
          information,
        };
      }),
    ];
  }

  private fetchAssets(clientId: string, categoryUids: AssetCategory[]): Observable<Asset[]> {
    return this.assetsApiService.get({ clientId, limit: this.ASSETS_AMOUNT_TO_FETCH, categoryUids }).pipe(
      take(1),
      tap(assets => {
        this.fetchedAssetIds = assets.map(a => a.id);
      }),
    );
  }

  mapAssetsToUploadedImageData(assets: Asset[], useOriginalIfNoDefault: boolean) {
    return combineLatest([
      this.subscriptionsFacade.isPaidSubscriptionActive$,
      this.subscriptionsFacade.pricePlansLoading$,
    ]).pipe(
      filter(([, pricePlansLoading]) => !pricePlansLoading),
      take(1),
      map(([isPaidSubscriptionActive]) =>
        this.uploadedImageDataService.mapAssetsToUploadedImagesData(
          assets,
          isPaidSubscriptionActive,
          useOriginalIfNoDefault,
        ),
      ),
    );
  }

  private sendAssetsToEditor(assets: Asset[], useOriginalIfNoDefault: boolean) {
    this.mapAssetsToUploadedImageData(assets, useOriginalIfNoDefault)
      .pipe(
        tap(uploadedImagesData => {
          this.designEditorCommunicationService.sendPostMessage(
            EditorInboundEventType.setUploadedImages,
            uploadedImagesData,
          );
        }),
      )
      .subscribe();
  }
}
